import { useEffect, useRef, useState, useCallback } from 'react';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { Capacitor } from '@capacitor/core';
import { apiVariables } from '../services/api/apiVariables';
import { getSensitiveData } from '../utils/secureStorage';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

interface UseSignalROptions {
  onReceiveMessage?: (message: any) => void;
  onReceiveGroupMessage?: (message: any) => void;
  onMessagesRead?: (payload: any) => void;
  onUserOnline?: (payload: any) => void;
  onUserOffline?: (payload: any) => void;
  onUserTyping?: (payload: any) => void;
  autoConnect?: boolean;
}

interface UseSignalRReturn {
  connection: HubConnection | null;
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  invoke: <T = any>(methodName: string, ...args: any[]) => Promise<T>;
  joinUserGroup: (userId: string) => Promise<void>;
  joinConversation: (conversationId: number) => Promise<void>;
  leaveConversation: (conversationId: number) => Promise<void>;
  joinConversationGroup: (conversationId: number) => Promise<void>;
  leaveConversationGroup: (conversationId: number) => Promise<void>;
  joinGroup: (groupId: number) => Promise<void>;
  leaveGroup: (groupId: number) => Promise<void>;
  joinGroupChat: (groupId: number) => Promise<void>;
  leaveGroupChat: (groupId: number) => Promise<void>;
  sendMessage: (payload: {
    receiverId: string;
    conversationId?: number;
    messageContent: string;
  }) => Promise<void>;
  sendGroupMessage: (payload: {
    groupId: number;
    conversationId: number;
    messageContent: string;
    messageType: string;
    attachmentUrl?: string | null;
  }) => Promise<void>;
  sendBroadcastMessage: (payload: {
    receiverIds: string[];
    messageContent: string;
  }) => Promise<void>;
  markMessagesAsRead: (conversationId: number, messageIds: number[]) => Promise<void>;
  sendTypingIndicator: (conversationId: number, isTyping: boolean) => Promise<void>;
}

export function useSignalR(options: UseSignalROptions = {}): UseSignalRReturn {
  const {
    onReceiveMessage,
    onReceiveGroupMessage,
    onMessagesRead,
    onUserOnline,
    onUserOffline,
    onUserTyping,
    autoConnect = false,
  } = options;

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const connectionRef = useRef<HubConnection | null>(null);
  const isConnectingRef = useRef(false);
  const listenersRegisteredRef = useRef(false);
  const currentUserIdRef = useRef<string>('');

  // Track if component is mounted
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load current user ID
  useEffect(() => {
    getSensitiveData('userId').then((userId) => {
      if (userId) {
        currentUserIdRef.current = userId;
      }
    });
  }, []);

  const registerListeners = useCallback((connection: HubConnection) => {
    if (listenersRegisteredRef.current) {
      return;
    }

    // ReceiveMessage - for private chat and one-to-many direct broadcasts
    if (onReceiveMessage) {
      connection.on('ReceiveMessage', (message) => {
        onReceiveMessage(message);
      });
    }

    // ReceiveGroupMessage - for group chat
    if (onReceiveGroupMessage) {
      connection.on("ReceiveGroupMessage", (msg) => { onReceiveGroupMessage?.(msg);  });
    }

    // MessagesRead - read receipts
    if (onMessagesRead) {
      connection.on('MessagesRead', onMessagesRead);
    }

    // UserOnline - online status
    if (onUserOnline) {
      connection.on('UserOnline', onUserOnline);
    }

    // UserOffline - offline status
    if (onUserOffline) {
      connection.on('UserOffline', onUserOffline);
    }

    // UserTyping - typing indicator
    if (onUserTyping) {
      connection.on('UserTyping', onUserTyping);
    }

    listenersRegisteredRef.current = true;
  }, [onReceiveMessage, onReceiveGroupMessage, onMessagesRead, onUserOnline, onUserOffline, onUserTyping]);

  const connect = useCallback(async () => {
    if (connectionRef.current?.state === HubConnectionState.Connected) {
      return;
    }

    if (isConnectingRef.current) {
      return;
    }

    isConnectingRef.current = true;

    try {
      const [authToken, storedUserId] = await Promise.all([
        getSensitiveData('authToken'),
        getSensitiveData('userId'),
      ]);
      if (!authToken) {
        throw new Error('No auth token available');
      }
      if (storedUserId) {
        currentUserIdRef.current = storedUserId;
      }


      const connection = new HubConnectionBuilder()
        .withUrl(apiVariables.CHAT_HUB_URL, {
          skipNegotiation: Capacitor.isNativePlatform(),
          withCredentials: true,
          accessTokenFactory: async () => (await getSensitiveData('authToken')) ?? '',
          transport: Capacitor.isNativePlatform()
            ? HttpTransportType.WebSockets
            : HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      // Setup lifecycle handlers
      connection.onreconnecting(() => {
        if (isMountedRef.current) {
          setConnectionStatus('reconnecting');
        }
      });


      // const sendGroupTypingIndicator = useCallback(
      //   async (groupId: number,isTyping: boolean
      //     ) => { await invoke( "GroupTyping", groupId, isTyping
      //       ); }, [invoke]);

      connection.onreconnected(async () => {
        if (!isMountedRef.current) return;
        
        setConnectionStatus('connected');
        
        // Rejoin user group after reconnect
        if (currentUserIdRef.current) {
          try {
            await connection.invoke('JoinUserGroup', currentUserIdRef.current);
          } catch (error) {
            console.error('Failed to rejoin user group:', error);
          }
        }
      });

      connection.onclose(() => {
        if (isMountedRef.current) {
          setConnectionStatus('disconnected');
        }
        listenersRegisteredRef.current = false;
      });

      // Register event listeners
      registerListeners(connection);

      // Start connection
      await connection.start();

      if (!isMountedRef.current) {
        await connection.stop();
        return;
      }

      connectionRef.current = connection;
      setConnectionStatus('connected');

      // Join user group
      if (currentUserIdRef.current) {
        try {
          await connection.invoke('JoinUserGroup', currentUserIdRef.current);
        } catch (error) {
          console.error('Failed to join user group:', error);
        }
      }
    } catch (error) {
      console.error('SignalR connection failed:', error);
      if (isMountedRef.current) {
        setConnectionStatus('disconnected');
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [registerListeners]);

  const disconnect = useCallback(async () => {
    const connection = connectionRef.current;
    if (!connection) return;

    connectionRef.current = null;
    listenersRegisteredRef.current = false;
    setConnectionStatus('disconnected');

    try {
      await connection.stop();
    } catch (error) {
      console.error('Error stopping SignalR connection:', error);
    }
  }, []);

  const invoke = useCallback(async <T = any>(methodName: string, ...args: any[]): Promise<T> => {
    const connection = connectionRef.current;
    
    if (!connection || connection.state !== HubConnectionState.Connected) {
      throw new Error('SignalR connection is not established');
    }

    return connection.invoke<T>(methodName, ...args);
  }, []);

  const joinUserGroup = useCallback(async (userId: string) => {
    await invoke('JoinUserGroup', userId);
  }, [invoke]);

  const joinConversation = useCallback(async (conversationId: number) => {
    await invoke('JoinConversation', conversationId);
  }, [invoke]);

  const leaveConversation = useCallback(async (conversationId: number) => {
    await invoke('LeaveConversation', conversationId);
  }, [invoke]);

  const joinConversationGroup = useCallback(async (conversationId: number) => {
    await invoke('JoinConversationGroup', conversationId);
  }, [invoke]);

  const leaveConversationGroup = useCallback(async (conversationId: number) => {
    await invoke('LeaveConversationGroup', conversationId);
  }, [invoke]);

  // const joinGroup = useCallback(async (groupId: number) => {
  //   await invoke('JoinGroup', groupId);
  // }, [invoke]);

  // const leaveGroup = useCallback(async (groupId: number) => {
  //   await invoke('LeaveGroup', groupId);
  // }, [invoke]);

  const joinGroup = useCallback(async (groupId: number) => {
  //await invoke('JoinGroupChat', groupId);
  await invoke("JoinGroupChat", groupId);
}, [invoke]);

const leaveGroup = useCallback(async (groupId: number) => {
  await invoke('LeaveGroupChat', groupId);
}, [invoke]);

  const sendMessage = useCallback(async (payload: {
    receiverId: string;
    conversationId?: number;
    messageContent: string;
  }) => {
    await invoke('SendMessage', payload);
  }, [invoke]);

  const sendGroupMessage = useCallback(async (payload: {
    groupId: number;
    conversationId: number;
    messageContent: string;
    messageType: string;
    attachmentUrl?: string | null;
  }) => {
    await invoke('SendGroupMessage', payload);
  }, [invoke]);

  const sendBroadcastMessage = useCallback(async (payload: {
    receiverIds: string[];
    messageContent: string;
  }) => {
    const uniqueReceiverIds = Array.from(new Set(payload.receiverIds.filter(Boolean)));
    const hubPayload = {
      userIds: uniqueReceiverIds,
      messageContent: payload.messageContent,
      sendSeparately: true,
    };
    await invoke('SendBroadcastMessage', hubPayload);
  }, [invoke]);

  const markMessagesAsRead = useCallback(async (conversationId: number, messageIds: number[]) => {
    await invoke('MarkMessagesAsRead', conversationId, messageIds);
  }, [invoke]);

  const sendTypingIndicator = useCallback(async (conversationId: number, isTyping: boolean) => {
    await invoke('Typing', conversationId, isTyping);
  }, [invoke]);

  // Auto-connect if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    connection: connectionRef.current,
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    connect,
    disconnect,
    invoke,
    joinUserGroup,
    joinConversation,
    leaveConversation,
    joinConversationGroup,
    leaveConversationGroup,
    joinGroup,
    leaveGroup,
    joinGroupChat: joinGroup,
    leaveGroupChat: leaveGroup,
    sendMessage,
    sendGroupMessage,
    sendBroadcastMessage,
    markMessagesAsRead,
    sendTypingIndicator,
  };
}
